import { Box, Divider, Typography } from "@mui/material";
import { ReactNode } from "react";
import LinearProgress from "components/LinearProgress";
import { ColumnHeader } from "./TabPanel";

// Types for resources
type Quantity = {
  cpu?: string | number | null;
  memory?: string | number | null;
};

export type Resources = {
  requests?: Quantity | null;
  limits?: Quantity | null;
};

export type ResourcesByKey = Record<string, Resources>;

export type ResourcesContentProps = {
  isFetching: boolean;
  error: unknown;
  noData: boolean;
  orderedKeys: string[];
  resourcesByKey: ResourcesByKey;
  formatDeploymentKey: (key: string) => string;
  formatMemory: (raw: string | number | null | undefined) => string;
  renderActionsForKey?: (key: string) => ReactNode;
};

export default function ResourcesContent({
  isFetching,
  error,
  noData,
  orderedKeys,
  resourcesByKey,
  formatDeploymentKey,
  formatMemory,
  renderActionsForKey,
}: ResourcesContentProps) {
  if (isFetching) return <LinearProgress />;
  if (error) return <Box sx={{ m: 2 }}>Failed to load resources.</Box>;
  if (noData)
    return <Box sx={{ m: 2 }}>No Resources available for this cluster.</Box>;

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      {orderedKeys.map((k) => {
        const res = resourcesByKey[k];
        return (
          <Box key={k} sx={{ border: "1px solid #eee", borderRadius: 1, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle1" sx={{ flex: 1 }}>
                {formatDeploymentKey(k)}
              </Typography>
              <Box sx={{ ml: 2 }}>
                {renderActionsForKey ? renderActionsForKey(k) : null}
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  border: "1px solid #e3f2fd",
                  backgroundColor: "#f7fbff",
                  p: 1.5,
                  borderRadius: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: 0.3,
                    color: "primary.main",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  Requests
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    rowGap: 1,
                  }}
                >
                  <ColumnHeader>CPU</ColumnHeader>
                  <Typography>{res?.requests?.cpu ?? "-"}</Typography>
                  <ColumnHeader>Memory</ColumnHeader>
                  <Typography>{formatMemory(res?.requests?.memory)}</Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  border: "1px solid #ffebee",
                  backgroundColor: "#fffafa",
                  p: 1.5,
                  borderRadius: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: 0.3,
                    color: "error.main",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  Limits
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    rowGap: 1,
                  }}
                >
                  <ColumnHeader>CPU</ColumnHeader>
                  <Typography>{res?.limits?.cpu ?? "-"}</Typography>
                  <ColumnHeader>Memory</ColumnHeader>
                  <Typography>{formatMemory(res?.limits?.memory)}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
